import { FC, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminClaimsApi } from "../../http/claims/api";
import { ClaimNoCarDependency, getClaimStateColor, getClaimStatuses } from "../../http/claims/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric } from "../../types/utils";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import Panel from "../ui/Panel";
import { State } from "../ui/State";
import Table from "../ui/Table";
import DateColumnFilter from "../ui/columnFilters/DateColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import ClaimHistoryDialog from "./ClaimHistoryDialog";
import ClaimStats from "./ClaimStats";
import ClaimTypes from "./claimTypes/ClaimTypes";
import DeleteClaimDialog from "./DeleteClaimDialog";

const CLAIM_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "number",
  "status",
  "openingDate",
  "closingDate",
  "incidentDate",
  "type.id",
  "type.name",
  "versionNumber",
  "driver",
  "subsidiary",
] as const;
type ClaimField = (typeof CLAIM_FIELDS)[number];
type ClaimSearch = NestedPick<ClaimNoCarDependency, ClaimField>;

type ClaimsProps = {
  onUpdate?: () => void;
  redirectUrl?: string;
};

const Claims: FC<ClaimsProps> = ({ onUpdate, redirectUrl }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const claimsApi = useAdminClaimsApi();
  const filters = useListFilters({ isSearchable: true, prefix: "cl" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { id: carId } = useParams<{ id: string }>();

  const [showClaimTypes, setShowClaimTypes] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ClaimSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<ClaimSearch>();
  const navigate = useNavigate();

  const query: PaginationQueryWithFields<ClaimSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: CLAIM_FIELDS,
  };

  const result = claimsApi.useAdminClaims(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: claimsApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const params = new URLSearchParams();
  carId && params.set("car_id", carId);
  redirectUrl && params.set("redirect_uri", redirectUrl);

  const navToEdit = (item: ClaimSearch) => {
    navigate(`/admin/claims/${item.id}?${params.toString()}`);
  };

  const getRowActions = (item: ClaimSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.claims.update) {
      actions.push(getEditAction(i18n, () => navToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.claims.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  const statuses = getClaimStatuses(i18n.t);

  const content = (
    <>
      <Table<ClaimSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: !!userSettings.permissions.claims.insert && (
            <Button
              buttonProps={{
                onClick: () => navigate(`/admin/claims/new?${params.toString()}`),
              }}
              glyph={SvgAdd}
            />
          ),
          secondary: !!userSettings.permissions.claimTypes.read && (
            <Button buttonProps={{ onClick: () => setShowClaimTypes(true) }}>{i18n.t.common.manageClaimTypes}</Button>
          ),
        }}
        onRowClick={(item, event) => {
          event.preventDefault();
          navToEdit(item);
        }}
        head={
          <>
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="claim.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="claim.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <Table.Th
              title={i18n.t.drivers.singular}
              fieldName="claim.driver.fullName"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            {!carId && (
              <Table.Th
                title={i18n.t.subsidiaries.singular}
                fieldName="claim.car.subsidiary.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
            )}
            <Table.Th
              title={i18n.t.common.number}
              fieldName="claim.number"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th
              title={i18n.t.common.type}
              fieldName="claim.type.name"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.state}
              fieldName="claim.status"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
              formatter={(value) => statuses.find((x) => x.value == value)?.label}
            />
            <Table.Th
              title={i18n.t.common.openingDate}
              fieldName="claim.openingDate"
              order={orderOptions}
              filters={filterOptions}
              filter={DateColumnFilter}
              formatter={(value) =>
                typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
              }
            />
            <Table.Th
              title={i18n.t.common.closingDate}
              fieldName="claim.closingDate"
              order={orderOptions}
              filters={filterOptions}
              filter={DateColumnFilter}
              formatter={(value) =>
                typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
              }
            />
            <Table.Th
              title={i18n.t.claims.incidentDate}
              fieldName="claim.incidentDate"
              order={orderOptions}
              filters={filterOptions}
              filter={DateColumnFilter}
              formatter={(value) =>
                typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
              }
            />
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.vehicleIdentificationNumber}
                    </CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td>
              {!!userSettings.permissions.users.read ? (
                <CustomLink to={`/admin/users/${item.driver?.id}`}>
                  {item.driver?.fullName}
                </CustomLink>
              ) : (
                item.driver?.fullName
              )}
            </td>
            {!carId && (
              <td>
                {item.subsidiary && (
                  <CustomLink to={`/admin/subsidiaries/${item.subsidiary.id}`}>
                    {item.subsidiary.name}
                  </CustomLink>
                )}
              </td>
            )}
            <td>{item.number}</td>
            <td>{item.type.name}</td>
            <td>
              <State
                text={statuses.find((x) => x.value == item.status)?.label ?? ""}
                color={getClaimStateColor(item.status)}
              />
            </td>
            <td>{formatDateNumeric(item.openingDate, i18n.language)}</td>
            <td>{item.closingDate ? formatDateNumeric(item.closingDate, i18n.language) : ""}</td>
            <td>{formatDateNumeric(item.incidentDate, i18n.language)}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {showClaimTypes && <ClaimTypes onClose={() => setShowClaimTypes(false)} />}

      {!!itemToShowVersions && (
        <ClaimHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!itemToDelete && (
        <DeleteClaimDialog
          claimId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.claims.plural} />
      <Panel>
        <ClaimStats />
        {content}
      </Panel>
    </>
  );
};

export default Claims;
